import { useRef, useState } from 'react'

import { Box, Button, Tabs } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'
import first from 'lodash/first'
import map from 'lodash/map'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import SavedShiftsSection from 'modules/shift_management/components/ShiftManagement/AddShiftSidepanel/SavedShiftsSection'
import { useAddShiftPreview } from 'modules/shift_management/hooks/useAddShiftPreview'
import useEditEmployees from 'modules/shift_management/hooks/useEditEmployees'
import { buildEditShiftsParameters } from 'modules/shift_management/hooks/useEditShifts'
import useEditShifts from 'modules/shift_management/hooks/useEditShifts'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { useReassignShifts } from 'modules/shift_management/hooks/useReassignShifts'
import useReplaceSavedShifts from 'modules/shift_management/hooks/useReplaceSavedShifts'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { buildAddShiftsInputParameters } from 'modules/shift_management/lib/buildAddShiftsInput'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import ShiftsEditForm from 'modules/shift_management/lib/forms/ShiftsEditForm'
import {
  onAddPlannedBreak,
  onDeletePlannedBreak,
} from 'modules/shift_management/lib/managePlannedBreaks'
import { SYMBOLIC_MIXED_VALUE, type Shift, type TabsType } from 'modules/shift_management/lib/types'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'

import EditShiftCustomSection from './EditShiftCustomSection'
import {
  formData,
  getDefaultShiftId,
  getTemplateId,
  hasMixedValues,
  plannedBreakForm,
} from './utils'

interface Props {
  shifts: Shift[]
  onClose: () => void
}

const EditShiftSidepanel = ({ shifts, onClose }: Props) => {
  const [{ view }] = useSchedule()
  const shiftIds = uniq(shifts.map((s) => s.id))
  const canEditBreaks = shiftIds.length === 1
  const scrollToErrorsTimeout = useRef<ReturnType<typeof setTimeout>>()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const { isTimeFormat24H } = useIsTimeFormat24H()

  const { loading, selectableEmployeeIds } = useSelectableEmployees(view.startAt, view.endAt)

  const initialAttributes = formData(shifts, view)
  const customShiftForm = useLazyRef(() => new ShiftsEditForm(initialAttributes)).current
  const uniqNotes = map(uniqBy(shifts, 'notes'), 'notes')
  const mixedValues = hasMixedValues(initialAttributes, uniqNotes)

  const {
    selectedEmployeeIds: selectedEmployees,
    setSelectedEmployees,
    editingOneEmployee,
    editingOriginal,
    isOriginalIncluded,
    daysByEmployeeCustom,
    daysByEmployeeSaved,
    canReassignShifts,
    canReplaceShifts,
    showReassignMessage,
    preselectedSavedShifts,
    commonSelectorsForm,
    defaultAttributesGraphQL,
  } = useEditEmployees(shifts, mixedValues)

  const [selectedTab, setSelectedTab] = useState<TabsType>('custom-shift')
  const [showSavedShiftsError, setShowSavedShiftsError] = useState(false)
  const isSavedShiftTab = selectedTab === 'saved-shifts'

  // Unless there is only 1 shift, we don't allow editing the breaks.
  // If that shift is default and we are reassigning the shift to another
  // employee, we don't enable the form to edit breaks because saved shifts breaks
  // are created automatically when adding those shifts to the new employees
  // and therefore breaks would be duplicated.
  const showBreaksForm = canEditBreaks ? (shifts[0]?.isDefault ? editingOriginal : true) : false

  const extraHoursValue = customShiftForm.get('extraHours').value
  const extraHours = extraHoursValue === '' ? null : Boolean(extraHoursValue)
  const editShiftsParams = buildEditShiftsParameters(
    shifts,
    customShiftForm.get('name'),
    customShiftForm.get('startAt'),
    customShiftForm.get('endAt'),
    extraHours,
    customShiftForm.get('locationId'),
    customShiftForm.get('workAreaId'),
    customShiftForm.get('notes'),
    uniqNotes.length
  )

  const defaultId = getDefaultShiftId(shifts)
  const templateId = getTemplateId(shifts)

  const assignShiftsParams = buildAddShiftsInputParameters({
    view,
    hasSelection: false,
    defaultAttributesByDate: {},
    name: customShiftForm.get('name').value,
    unparsedStartTime: customShiftForm.get('startAt').value,
    unparsedEndTime: customShiftForm.get('endAt').value,
    daysByRow: daysByEmployeeCustom,
    defaultShiftId: defaultId,
    extraHours: customShiftForm.get('extraHours').value,
    notes: customShiftForm.get('notes').value,
    locationId: customShiftForm.get('locationId').value,
    workAreaId: customShiftForm.get('workAreaId').value,
    color: null,
    templateWeekId: templateId,
    unassignedShiftId: null,
    state: 'draft',
    leaveId: undefined,
    companyId: currentCompanyId,
  })

  // Breaks logic
  const { plannedBreaks, plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { shiftIds },
    skip: !canEditBreaks,
  })

  const readOnlyPlannedBreaks = shifts[0] && plannedBreaksCollection.filterByShift(shifts[0].id)

  const initPlannedBreaks = () => {
    if (!canEditBreaks) return []

    const timezone = first(shifts)?.employeeTimeZone
    return plannedBreaks.map((pb) => plannedBreakForm(pb, timezone, isTimeFormat24H))
  }

  // Setting plannedbreaks values into the form
  const [plannedBreakForms, setPlannedBreakForms] = useState(initPlannedBreaks())

  useEffect(() => {
    setPlannedBreakForms(initPlannedBreaks())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedBreaks.length])

  const onAddBreak = (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => {
    onAddPlannedBreak(
      breakConfiguration,
      breakType,
      plannedBreakForms,
      isTimeFormat24H,
      (value) => {
        setPlannedBreakForms(value)
      }
    )
  }

  const onDeleteBreak = (breakForm: PlannedBreakForm) => {
    onDeletePlannedBreak(breakForm, plannedBreakForms, setPlannedBreakForms)
  }

  const { onEdit, submitting: submittingCustomShift } = useEditShifts(
    editShiftsParams,
    plannedBreakForms,
    'side_panel'
  )

  const { onReassignShifts, submittingOnReassign } = useReassignShifts({
    shifts,
    editShiftsParams,
    assignShiftsParams,
    isOriginalIncluded,
    plannedBreakForms,
    through: 'side_panel',
  })

  const {
    forms: defaultShiftForms,
    onToggleShift,
    onSubmit: onSubmitDefaultShifts,
    submitting: submittingDefaultShifts,
  } = useReplaceSavedShifts({
    shifts,
    defaultAttributes: defaultAttributesGraphQL,
    daysByRow: daysByEmployeeSaved,
    preselectedSavedShifts,
    isOriginalIncluded,
    showReassignMessage,
  })

  const { cleanPreview } = useAddShiftPreview({
    customShiftForm: !mixedValues ? customShiftForm : undefined,
    daysByRow: daysByEmployeeSaved,
    defaultShiftForms,
    plannedBreakForms,
    selectedTab,
    // The shifts are visible editing from scratch (mixed values)
    // because the preview is not supported in those cases.
    shiftIdsToHide: isSavedShiftTab || !mixedValues ? shifts.map((s) => s.id) : [],
  })

  const handleErrors = (): boolean => {
    const isCommonValid = commonSelectorsForm.validate()
    if (isSavedShiftTab) {
      const hasSelected = defaultShiftForms.some((form) => form.isSelected)
      hasSelected ? setShowSavedShiftsError(false) : setShowSavedShiftsError(true)

      return isCommonValid && hasSelected
    } else {
      const isCustomValid = customShiftForm.validate()
      const isBreaksValid = plannedBreakForms.every((form) =>
        form.validate({
          startAt: customShiftForm.startAt,
          endAt: customShiftForm.endAt,
        })
      )

      return isCommonValid && isCustomValid && isBreaksValid
    }
  }

  const scrollToErrors = () => {
    // Timeout to wait for the error to be rendered
    clearTimeout(scrollToErrorsTimeout.current)
    scrollToErrorsTimeout.current = setTimeout(() => {
      const errorAnchors = document.getElementsByClassName('error-anchor')
      const firstError = errorAnchors[0]
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 100)
  }

  const handleOnSubmitCustomShift = async () => {
    !editingOneEmployee || editingOriginal ? await onEdit() : await onReassignShifts()
  }

  const handleClose = () => {
    cleanPreview()
    onClose()
  }

  const onSubmit = async () => {
    const isValid = handleErrors()
    if (!isValid) return scrollToErrors()

    selectedTab === 'custom-shift'
      ? await handleOnSubmitCustomShift()
      : await onSubmitDefaultShifts()
    handleClose()
  }

  const submitting = submittingCustomShift || submittingOnReassign || submittingDefaultShifts

  const renderCustomShiftSection = () => (
    <Box paddingTop="s24" gap="s24">
      <EditShiftCustomSection
        form={customShiftForm}
        commonSelectorsForm={commonSelectorsForm}
        mixedValuesByName={initialAttributes.name === null}
        mixedValuesByStartTime={!initialAttributes.startAt}
        mixedValuesByEndTime={!initialAttributes.endAt}
        mixedValuesByNotes={uniqNotes.length > 1}
        mixedValuesByLocation={initialAttributes.locationId === SYMBOLIC_MIXED_VALUE}
        mixedValuesByWorkArea={initialAttributes.workAreaId === SYMBOLIC_MIXED_VALUE}
        hasMixedValues={mixedValues}
        canReassignShifts={canReassignShifts}
        selectedEmployees={selectedEmployees}
        selectableEmployeeIds={selectableEmployeeIds}
        setSelectedEmployees={setSelectedEmployees}
        showReassignMessage={showReassignMessage}
        plannedBreakForms={canEditBreaks ? plannedBreakForms : undefined}
        readOnlyPlannedBreaks={readOnlyPlannedBreaks}
        showBreaksForm={showBreaksForm}
        onAddBreak={onAddBreak}
        onDeleteBreak={onDeleteBreak}
      />
    </Box>
  )

  return (
    <Sidepanel
      open
      locked={submitting}
      title={i18n.tp('shift_management.actions_sidepanel.edit_shift', { count: shifts.length })}
      footer={
        <>
          <Button onClick={handleClose} disabled={submitting} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={submitting} submit>
            {i18n.tp('shift_management.actions_sidepanel.edit_shift', { count: shifts.length })}
          </Button>
        </>
      }
      onClose={handleClose}
      closeLabel="Close"
    >
      {loading ? (
        <Loading />
      ) : // Avoid tabs when can't replace shifts and there is only 1 tab.
      canReplaceShifts ? (
        <Tabs
          tabsAlign="center"
          onTabClicked={(tabId: TabsType) => setSelectedTab(tabId)}
          currentId={selectedTab}
        >
          <Tabs.Tab
            id="saved-shifts"
            key="saved-shifts"
            label={i18n.t('shift_management.actions_sidepanel.tabs.saved_shift')}
          >
            <Box paddingTop="s24" gap="s24">
              <SavedShiftsSection
                commonSelectorsForm={commonSelectorsForm}
                forms={defaultShiftForms}
                defaultAttributes={defaultAttributesGraphQL}
                selectableEmployeeIds={selectableEmployeeIds}
                selectedEmployees={selectedEmployees}
                setSelectedEmployees={setSelectedEmployees}
                onToggleShift={onToggleShift}
                canReassignShifts={canReassignShifts}
                showError={showSavedShiftsError}
                edit
              />
            </Box>
          </Tabs.Tab>
          <Tabs.Tab
            id="custom-shift"
            key="custom-shift"
            label={i18n.t('shift_management.actions_sidepanel.tabs.custom_shift')}
          >
            {renderCustomShiftSection()}
          </Tabs.Tab>
        </Tabs>
      ) : (
        renderCustomShiftSection()
      )}
    </Sidepanel>
  )
}

export default observer(EditShiftSidepanel)
